import React, { useState, useEffect, useMemo } from 'react';
import { Routes, Route, Link, useParams, useNavigate, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useQuery, useQueryClient } from 'react-query';
import clsx from 'clsx';
import PageHeader from '../../components/ui/PageHeader';
import UiCard from '../../components/ui/UiCard';
import FilterSelect from '../../components/ui/FilterSelect';
import { paginationItems, FORMA_LINK_BTN } from '../../utils/listPageUi';
import { fetchFormationEnrollmentBundle } from '../../utils/formationEnrollmentBundle';
import { invalidateFormationGraph } from '../../utils/invalidateFormationGraph';
import { FORMATION_ENROLLMENT_BUNDLE_KEY, FORMATION_STUDENTS_CATALOG_KEY } from '../../utils/queryKeys';
import { formationAPI, spaEntityAPI } from '../../services/api';
import { useLanguage } from '../../contexts/LanguageContext';
import { confirmDialog } from '../../utils/confirmBridge';
import notify from '../../utils/notify';
import {
  PlusIcon,
  MagnifyingGlassIcon,
  UserGroupIcon,
  EyeIcon,
  PencilIcon,
  TrashIcon,
  PhoneIcon,
  IdentificationIcon,
  CalendarDaysIcon,
  CheckCircleIcon,
  XCircleIcon,
  AcademicCapIcon,
  ArrowLeftIcon,
  TagIcon,
  XMarkIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
} from '@heroicons/react/24/outline';

const StudentDetail = ({ student }) => {
  const { t } = useLanguage();
  const { data: bundle } = useQuery(FORMATION_ENROLLMENT_BUNDLE_KEY, fetchFormationEnrollmentBundle, {
    staleTime: 15_000,
  });
  const batchById = useMemo(() => {
    const m = {};
    (bundle?.batches || []).forEach((b) => {
      if (b?.id) m[b.id] = b;
    });
    return m;
  }, [bundle?.batches]);
  const activeEnrollments = useMemo(
    () =>
      (bundle?.enrollments || []).filter((e) => e.studentId === student?.id && e.status === 'active'),
    [bundle?.enrollments, student?.id]
  );

  const statusLabel =
    student?.status === 'active'
      ? t('studentsPage.statusActiveLbl')
      : student?.status === 'suspended'
        ? t('studentsPage.statusSuspendedLbl')
        : student?.status === 'inactive'
          ? t('studentsPage.statusInactiveLbl')
          : student?.status
            ? student.status
            : t('studentsPage.detailStatusUnknown');

  return (
    <div className="rounded-3xl border border-slate-200 bg-white shadow-soft dark:border-slate-700 dark:bg-slate-900/75 p-6">
      <div className="flex flex-col items-center mb-6">
        <div className="h-24 w-24 rounded-full bg-forma-500/15 flex items-center justify-center mb-4">
          {student?.photo ? (
            <img src={student.photo} alt={student.name || t('studentsPage.detailNameMissing')} className="h-24 w-24 rounded-full object-cover" />
          ) : (
            <AcademicCapIcon className="h-12 w-12 text-forma-600 dark:text-forma-400" />
          )}
        </div>
        <h2 className="text-xl font-semibold text-slate-900 dark:text-slate-50 mb-1">{student?.name || t('studentsPage.detailNameMissing')}</h2>
        <p className="text-slate-600 dark:text-slate-400 text-sm">{student?.studentCode || t('studentsPage.detailCodeMissing')}</p>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
        <div className="bg-slate-100/80 dark:bg-slate-800/40 rounded-lg p-4">
          <div className="flex items-center mb-2">
            <PhoneIcon className="h-5 w-5 text-forma-600 dark:text-forma-400 mr-2" />
            <span className="text-slate-700 dark:text-slate-300 text-sm">{t('studentsPage.detailPhoneLabel')}</span>
          </div>
          <p className="text-slate-900 dark:text-slate-50">{student?.phone || t('studentsPage.notProvidedShort')}</p>
        </div>
        <div className="bg-slate-100/80 dark:bg-slate-800/40 rounded-lg p-4">
          <div className="flex items-center mb-2">
            <IdentificationIcon className="h-5 w-5 text-forma-600 dark:text-forma-400 mr-2" />
            <span className="text-slate-700 dark:text-slate-300 text-sm">{t('studentsPage.detailEmailLabel')}</span>
          </div>
          <p className="text-slate-900 dark:text-slate-50">{student?.email || t('studentsPage.notProvidedShort')}</p>
        </div>
        <div className="bg-slate-100/80 dark:bg-slate-800/40 rounded-lg p-4">
          <div className="flex items-center mb-2">
            <CalendarDaysIcon className="h-5 w-5 text-forma-600 dark:text-forma-400 mr-2" />
            <span className="text-slate-700 dark:text-slate-300 text-sm">{t('studentsPage.detailJoinDateLabel')}</span>
          </div>
          <p className="text-slate-900 dark:text-slate-50">{student?.joinDate ? new Date(student.joinDate).toLocaleDateString() : t('studentsPage.notProvidedShort')}</p>
        </div>
        <div className="bg-slate-100/80 dark:bg-slate-800/40 rounded-lg p-4">
          <div className="flex items-center mb-2">
            <CheckCircleIcon className="h-5 w-5 text-forma-600 dark:text-forma-400 mr-2" />
            <span className="text-slate-700 dark:text-slate-300 text-sm">{t('studentsPage.detailStatusLabel')}</span>
          </div>
          <p className="text-slate-900 dark:text-slate-50 capitalize">{statusLabel}</p>
        </div>
      </div>
      
      <div className="bg-slate-100/80 dark:bg-slate-800/40 rounded-lg p-4 mb-6">
        <h3 className="text-lg font-medium text-slate-900 dark:text-slate-50 mb-4">{t('studentsPage.detailActiveEnrollmentsTitle')}</h3>
        {(() => {
          if (activeEnrollments.length === 0) {
            return <p className="text-slate-600 dark:text-slate-400 italic">{t('studentsPage.detailNoActiveEnrollment')}</p>;
          }
          
          return (
            <div className="space-y-4">
              {activeEnrollments.map(enrollment => {
                const batch = batchById[enrollment.batchId];
                if (!batch) return null;
                
                return (
                  <div key={enrollment.id} className="bg-slate-100/80 dark:bg-slate-800/40 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="text-slate-900 dark:text-slate-50 font-medium">{batch.courseName}</h4>
                      <span className="px-2 py-1 bg-green-500/20 text-green-400 rounded-full text-xs">
                        {t('studentsPage.detailBadgeActive')}
                      </span>
                    </div>
                    <p className="text-slate-600 dark:text-slate-400 text-sm mb-3">{batch.batchName}</p>
                    
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <span className="text-slate-600 dark:text-slate-400">{t('studentsPage.detailScheduleLabel')}</span>
                        <p className="text-slate-900 dark:text-slate-50">{batch.days.join(', ')}</p>
                        <p className="text-slate-900 dark:text-slate-50">{batch.startTime} - {batch.endTime}</p>
                      </div>
                      <div>
                        <span className="text-slate-600 dark:text-slate-400">{t('studentsPage.detailPeriodLabel')}</span>
                        <p className="text-slate-900 dark:text-slate-50">
                          {t('studentsPage.detailPeriodFrom')} {new Date(batch.startDate).toLocaleDateString()}
                        </p>
                        <p className="text-slate-900 dark:text-slate-50">
                          {t('studentsPage.detailPeriodTo')} {new Date(batch.endDate).toLocaleDateString()}
                        </p>
                      </div>
                      <div>
                        <span className="text-slate-600 dark:text-slate-400">{t('studentsPage.detailTrainerLabel')}</span>
                        <p className="text-slate-900 dark:text-slate-50">{batch.trainerName}</p>
                      </div>
                      <div>
                        <span className="text-slate-600 dark:text-slate-400">{t('studentsPage.detailRoomLabel')}</span>
                        <p className="text-slate-900 dark:text-slate-50">{batch.room || t('studentsPage.detailRoomUndefined')}</p>
                      </div>
                    </div>
                    
                    <div className="mt-3">
                      <span className="text-slate-600 dark:text-slate-400 text-sm">{t('studentsPage.detailEnrollmentLabel')}</span>
                      <p className="text-slate-900 dark:text-slate-50 text-sm">
                        {new Date(enrollment.enrollmentDate).toLocaleDateString()}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
          );
        })()}
      </div>
    </div>
  );
};

const StudentForm = ({ isEditing = false, studentData = null }) => {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const queryClient = useQueryClient();

  const { data: bundle, refetch: refetchBundles } = useQuery(
    FORMATION_ENROLLMENT_BUNDLE_KEY,
    fetchFormationEnrollmentBundle,
    {
      staleTime: 1000,
      refetchInterval: 5000,
      refetchOnWindowFocus: true,
    }
  );

  const batchesData = useMemo(() => bundle?.batches ?? [], [bundle?.batches]);

  const getBatchWithEnrollment = (batchId) => batchesData.find((b) => b.id === batchId) || null;

  /** Sync editable fields when `studentData` loads from API (incl. `addressJson`). */
  useEffect(() => {
    if (!isEditing || !studentData) return;
    let address = {
      street: '',
      city: '',
      postalCode: '',
      state: '',
      country: 'Algérie',
    };
    if (studentData.addressJson) {
      try {
        const parsed =
          typeof studentData.addressJson === 'string'
            ? JSON.parse(studentData.addressJson)
            : studentData.addressJson;
        address = {
          street: parsed.street ?? '',
          city: parsed.city ?? '',
          postalCode: parsed.postalCode ?? '',
          state: parsed.state ?? '',
          country: parsed.country || 'Algérie',
        };
      } catch (_) {
        /* ignore */
      }
    } else if (studentData.address && typeof studentData.address === 'object') {
      address = {
        street: studentData.address.street ?? '',
        city: studentData.address.city ?? '',
        postalCode: studentData.address.postalCode ?? '',
        state: studentData.address.state ?? '',
        country: studentData.address.country || 'Algérie',
      };
    }
    const jd =
      typeof studentData.joinDate === 'string'
        ? studentData.joinDate.slice(0, 10)
        : studentData.joinDate
          ? new Date(studentData.joinDate).toISOString().slice(0, 10)
          : new Date().toISOString().split('T')[0];
    setFormData((prev) => ({
      ...prev,
      name: studentData.name ?? prev.name,
      email: studentData.email ?? prev.email,
      phone: studentData.phone ?? prev.phone,
      status: studentData.status ?? prev.status,
      studentCode: studentData.studentCode ?? prev.studentCode,
      joinDate: jd,
      address,
    }));
  }, [isEditing, studentData]);

  // Initialize formData first
  const [formData, setFormData] = useState({
    name: studentData?.name || "",
    email: studentData?.email || "",
    phone: studentData?.phone || "",
    status: studentData?.status || "active",
    studentCode: studentData?.studentCode || `ETU-${new Date().getFullYear()}-${Math.floor(Math.random() * 1000).toString().padStart(3, '0')}`,
    joinDate: studentData?.joinDate || new Date().toISOString().split('T')[0],
    selectedBatch: "", // NEW: Replace course/session with batch selection
    photo: null,
    // Address fields
    address: {
      street: studentData?.address?.street || "",
      city: studentData?.address?.city || "",
      postalCode: studentData?.address?.postalCode || "",
      state: studentData?.address?.state || "",
      country: studentData?.address?.country || "Algérie"
    }
  });

  // Process available batches
  const availableBatches = React.useMemo(() => {
    const batchesByCourse = {};

    if (Array.isArray(batchesData)) {
      batchesData.forEach((batch) => {
        if (batch.status === 'active' && !batch.isFull) {
          const label = batch.courseName || batch.courseId || '';
          if (!batchesByCourse[label]) {
            batchesByCourse[label] = [];
          }
          batchesByCourse[label].push(batch);
        }
      });
    }
    return batchesByCourse;
  }, [batchesData]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    // Handle nested address fields
    if (name.startsWith('address.')) {
      const addressField = name.split('.')[1];
      setFormData(prev => ({
        ...prev,
        address: {
          ...prev.address,
          [addressField]: value
        }
      }));
    } else {
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      await refetchBundles();
      const addressJson = JSON.stringify(formData.address);

      if (isEditing && studentData) {
        await formationAPI.updateStudent(studentData.id, {
          name: formData.name,
          email: formData.email,
          phone: formData.phone,
          status: formData.status,
          studentCode: formData.studentCode,
          joinDate: formData.joinDate,
          addressJson,
          photo: studentData.photo ?? null,
          currentCourseId: studentData.currentCourseId ?? studentData.currentCourse ?? null,
        });
      } else {
        const newStudentId = `student-${Date.now()}`;
        await formationAPI.createStudent({
          id: newStudentId,
          name: formData.name,
          email: formData.email,
          phone: formData.phone,
          status: formData.status,
          studentCode: formData.studentCode,
          joinDate: formData.joinDate,
          addressJson,
          photo: typeof formData.photo === 'string' ? formData.photo : null,
          currentCourseId: null,
        });

        if (formData.selectedBatch) {
          const batchWithEnrollment = getBatchWithEnrollment(formData.selectedBatch);

          if (!batchWithEnrollment) {
            notify.show(t('studentsPage.formAlertBatchNotFound'));
            return;
          }

          if (batchWithEnrollment.isFull) {
            notify.show(
              t('studentsPage.formAlertBatchFull', {
                batch: batchWithEnrollment.batchName,
                current: batchWithEnrollment.currentEnrollment,
                max: batchWithEnrollment.maxCapacity,
              })
            );
            return;
          }

          if (batchWithEnrollment.availableSpots <= 0) {
            notify.show(
              t('studentsPage.formAlertNoSpots', {
                batch: batchWithEnrollment.batchName,
                spots: batchWithEnrollment.availableSpots,
              })
            );
            return;
          }

          try {
            const enrollmentId = `enroll-${newStudentId}-${formData.selectedBatch}-${Date.now()}`;
            await formationAPI.createEnrollment({
              id: enrollmentId,
              studentId: newStudentId,
              batchId: formData.selectedBatch,
              enrollmentDate: new Date().toISOString(),
              status: 'active',
              paymentStatus: 'pending',
            });
            const refreshed = await fetchFormationEnrollmentBundle();
            const b = refreshed.batches?.find((x) => x.id === formData.selectedBatch);
            notify.show(
              t('studentsPage.formAlertEnrollmentSuccess', {
                name: formData.name,
                batch: b?.batchName ?? batchWithEnrollment.batchName,
                current: b?.currentEnrollment ?? batchWithEnrollment.currentEnrollment + 1,
                max: b?.maxCapacity ?? batchWithEnrollment.maxCapacity,
                remaining: b?.availableSpots ?? Math.max(batchWithEnrollment.availableSpots - 1, 0),
              })
            );
          } catch (error) {
            console.error('Error creating enrollment:', error);
            notify.show(t('studentsPage.formAlertEnrollmentFailed'));
            return;
          }
        }
      }

      await invalidateFormationGraph(queryClient);
      if (studentData?.id) {
        queryClient.invalidateQueries(['formation', 'student', studentData.id]);
      }

      navigate('/students');
    } catch (error) {
      console.error('Error saving student data:', error);
      notify.show(t('trainerForm.saveError'));
    }
  };

  return (
    <div className="rounded-3xl border border-slate-200 bg-white shadow-soft dark:border-slate-700 dark:bg-slate-900/75 p-6">
      <h2 className="text-xl font-semibold text-slate-900 dark:text-slate-50 mb-6">
        {isEditing ? t('studentsPage.formTitleEdit') : t('studentsPage.formTitleAdd')}
      </h2>
      
      <form className="space-y-8" onSubmit={handleSubmit}>
        {/* Section Informations personnelles */}
        <div className="bg-slate-100/80 dark:bg-slate-800/40 rounded-xl p-6 border border-slate-200 dark:border-slate-600">
          <h3 className="text-lg font-medium text-slate-900 dark:text-slate-50 mb-4 flex items-center">
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
            </svg>
            {t('studentsPage.formSectionPersonal')}
          </h3>
          
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-slate-700 dark:text-slate-300 text-sm font-medium mb-2">
                {t('studentsPage.formFullName')}
            </label>
            <input 
              type="text"
              name="name" 
              value={formData.name}
              onChange={handleChange}
              className="w-full bg-slate-100/80 dark:bg-slate-800/40 border border-slate-200 dark:border-slate-600 rounded-lg px-4 py-2.5 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-forma-500"
              required
            />
          </div>
          
          <div>
            <label className="block text-slate-700 dark:text-slate-300 text-sm font-medium mb-2">
                {t('studentsPage.formEmail')}
            </label>
            <input 
              type="email"
              name="email" 
              value={formData.email}
              onChange={handleChange}
              className="w-full bg-slate-100/80 dark:bg-slate-800/40 border border-slate-200 dark:border-slate-600 rounded-lg px-4 py-2.5 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-forma-500"
              required
            />
          </div>
          
          <div>
            <label className="block text-slate-700 dark:text-slate-300 text-sm font-medium mb-2">
                {t('studentsPage.formPhone')}
            </label>
            <input 
              type="text"
              name="phone" 
              value={formData.phone}
              onChange={handleChange}
              className="w-full bg-slate-100/80 dark:bg-slate-800/40 border border-slate-200 dark:border-slate-600 rounded-lg px-4 py-2.5 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-forma-500"
                placeholder={t('studentsPage.formPhonePlaceholder')}
              required
            />
          </div>
          
          <div>
            <label className="block text-slate-700 dark:text-slate-300 text-sm font-medium mb-2">
              {t('studentsPage.formStatus')}
            </label>
            <select 
              name="status"
              value={formData.status}
              onChange={handleChange}
              className="w-full bg-slate-100/80 dark:bg-slate-800/40 border border-slate-200 dark:border-slate-600 rounded-lg px-4 py-2.5 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-forma-500"
            >
              <option value="active">{t('studentsPage.statusActiveLbl')}</option>
              <option value="inactive">{t('studentsPage.statusInactiveLbl')}</option>
              <option value="suspended">{t('studentsPage.statusSuspendedLbl')}</option>
            </select>
            </div>
          </div>
        </div>

        {/* Section Adresse */}
        <div className="bg-slate-100/80 dark:bg-slate-800/40 rounded-xl p-6 border border-slate-200 dark:border-slate-600">
          <h3 className="text-lg font-medium text-slate-900 dark:text-slate-50 mb-4 flex items-center">
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
            {t('studentsPage.formSectionAddress')}
          </h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="md:col-span-2">
              <label className="block text-slate-700 dark:text-slate-300 text-sm font-medium mb-2">
                {t('studentsPage.formStreet')}
              </label>
              <input 
                type="text"
                name="address.street" 
                value={formData.address.street}
                onChange={handleChange}
                className="w-full bg-slate-100/80 dark:bg-slate-800/40 border border-slate-200 dark:border-slate-600 rounded-lg px-4 py-2.5 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-forma-500"
                placeholder={t('studentsPage.formStreetPlaceholder')}
              />
            </div>
            
            <div>
              <label className="block text-slate-700 dark:text-slate-300 text-sm font-medium mb-2">
                {t('studentsPage.formCity')}
              </label>
              <input 
                type="text"
                name="address.city" 
                value={formData.address.city}
                onChange={handleChange}
                className="w-full bg-slate-100/80 dark:bg-slate-800/40 border border-slate-200 dark:border-slate-600 rounded-lg px-4 py-2.5 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-forma-500"
                placeholder={t('studentsPage.formCityPlaceholder')}
              />
            </div>
            
            <div>
              <label className="block text-slate-700 dark:text-slate-300 text-sm font-medium mb-2">
                {t('studentsPage.formPostalCode')}
              </label>
              <input 
                type="text"
                name="address.postalCode" 
                value={formData.address.postalCode}
                onChange={handleChange}
                className="w-full bg-slate-100/80 dark:bg-slate-800/40 border border-slate-200 dark:border-slate-600 rounded-lg px-4 py-2.5 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-forma-500"
                placeholder={t('studentsPage.formPostalPlaceholder')}
              />
            </div>
            
            <div>
              <label className="block text-slate-700 dark:text-slate-300 text-sm font-medium mb-2">
                {t('studentsPage.formState')}
              </label>
              <input 
                type="text"
                name="address.state" 
                value={formData.address.state}
                onChange={handleChange}
                className="w-full bg-slate-100/80 dark:bg-slate-800/40 border border-slate-200 dark:border-slate-600 rounded-lg px-4 py-2.5 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-forma-500"
                placeholder={t('studentsPage.formCityPlaceholder')}
              />
            </div>
            
            <div>
              <label className="block text-slate-700 dark:text-slate-300 text-sm font-medium mb-2">
                {t('studentsPage.formCountry')}
              </label>
              <input 
                type="text"
                name="address.country" 
                value={formData.address.country}
                onChange={handleChange}
                className="w-full bg-slate-100/80 dark:bg-slate-800/40 border border-slate-200 dark:border-slate-600 rounded-lg px-4 py-2.5 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-forma-500"
              />
            </div>
          </div>
        </div>

        {/* Section Formation */}
        <div className="bg-slate-100/80 dark:bg-slate-800/40 rounded-xl p-6 border border-slate-200 dark:border-slate-600">
          <h3 className="text-lg font-medium text-slate-900 dark:text-slate-50 mb-4 flex items-center">
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.746 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
            </svg>
            {t('studentsPage.formSectionTraining')}
          </h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-slate-700 dark:text-slate-300 text-sm font-medium mb-2">
                <TagIcon className="h-4 w-4 inline mr-1" />
                {t('studentsPage.formBatchSelect')}
              </label>
              <select 
                name="selectedBatch"
                value={formData.selectedBatch}
                onChange={handleChange}
                className="w-full bg-slate-100/80 dark:bg-slate-800/40 border border-slate-200 dark:border-slate-600 rounded-lg px-4 py-2.5 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-forma-500"
              >
                <option value="">{t('studentsPage.formBatchPlaceholder')}</option>
                {Object.entries(availableBatches).map(([courseName, batches]) => (
                  <optgroup key={courseName} label={courseName}>
                    {batches.map(batch => {
                      const batchWithCapacity = getBatchWithEnrollment(batch.id);
                      const isFull = batchWithCapacity?.isFull || false;
                      const availableSpots = batchWithCapacity?.availableSpots || 0;
                      const currentEnrollment = batchWithCapacity?.currentEnrollment || 0;
                      const maxCapacity = batchWithCapacity?.maxCapacity || batch.maxCapacity;
                      
                      return (
                        <option 
                          key={batch.id} 
                          value={batch.id}
                          disabled={isFull}
                          style={isFull ? { color: '#ef4444', fontStyle: 'italic' } : {}}
                        >
                          {batch.batchName} • {batch.days.join(', ')} {batch.startTime}-{batch.endTime} • 
                          {isFull ? ` 🔴 ${t('studentsPage.formBatchFullMark')}` : ` 🟢 ${t('studentsPage.formBatchPlacesAvail', { n: availableSpots })}`} 
                          ({currentEnrollment}/{maxCapacity})
                        </option>
                      );
                    })}
                  </optgroup>
                ))}
              </select>
              {formData.selectedBatch && (() => {
                const selectedBatch = batchesData.find(b => b.id === formData.selectedBatch);
                return selectedBatch ? (
                  <div className="mt-2 p-3 bg-blue-500/10 border border-blue-500/20 rounded-lg">
                    <div className="text-blue-300 text-sm">
                      <strong>{t('studentsPage.formBatchDetailsTitle')}</strong>
                      <br />
                      <div className="grid grid-cols-2 gap-2 mt-2">
                        <div>
                          <span className="text-blue-200">{t('studentsPage.formLblCourse')}</span> {selectedBatch.courseName}
                        </div>
                        <div>
                          <span className="text-blue-200">{t('studentsPage.formLblTrainer')}</span> {selectedBatch.trainerName}
                        </div>
                        <div>
                          <span className="text-blue-200">{t('studentsPage.formLblSchedule')}</span> {selectedBatch.days.join(', ')}
                        </div>
                        <div>
                          <span className="text-blue-200">{t('studentsPage.formLblHours')}</span> {selectedBatch.startTime} - {selectedBatch.endTime}
                        </div>
                        <div>
                          <span className="text-blue-200">{t('studentsPage.formLblPeriod')}</span> {new Date(selectedBatch.startDate).toLocaleDateString()} {t('studentsPage.formLblPeriodSep')} {new Date(selectedBatch.endDate).toLocaleDateString()}
                        </div>
                        <div>
                          <span className="text-blue-200">{t('studentsPage.formLblSeats')}</span> {selectedBatch.currentEnrollment}/{selectedBatch.maxCapacity} ({t('studentsPage.formLblSeatsRemain', { n: selectedBatch.availableSpots })})
                        </div>
                        <div>
                          <span className="text-blue-200">{t('studentsPage.formLblRoom')}</span> {selectedBatch.room || t('studentsPage.detailRoomUndefined')}
                        </div>
                        <div>
                          <span className="text-blue-200">{t('studentsPage.formLblStatus')}</span> 
                          {(() => {
                            const batchCapacity = getBatchWithEnrollment(selectedBatch.id);
                            const isFull = batchCapacity?.isFull || false;
                            const availableSpots = batchCapacity?.availableSpots || 0;
                            const enrollmentRate = batchCapacity ? (batchCapacity.currentEnrollment / batchCapacity.maxCapacity) * 100 : 0;
                            
                            let statusColor, statusText, statusIcon;
                            
                            if (isFull) {
                              statusColor = 'bg-red-500/20 text-red-300';
                              statusText = t('studentsPage.formStatusFull');
                              statusIcon = '🔴';
                            } else if (enrollmentRate >= 90) {
                              statusColor = 'bg-orange-500/20 text-orange-300';
                              statusText = t('studentsPage.formStatusAlmostFull');
                              statusIcon = '🟠';
                            } else if (enrollmentRate >= 75) {
                              statusColor = 'bg-yellow-500/20 text-yellow-300';
                              statusText = t('studentsPage.formStatusFilling');
                              statusIcon = '🟡';
                            } else {
                              statusColor = 'bg-green-500/20 text-green-300';
                              statusText = t('studentsPage.formStatusOpen');
                              statusIcon = '🟢';
                            }
                            
                            return (
                              <span className={`ml-1 px-2 py-0.5 rounded text-xs ${statusColor}`}>
                                {statusIcon} {statusText} ({t('studentsPage.formPlacesParen', { n: availableSpots })})
                              </span>
                            );
                          })()}
                        </div>
                      </div>
                    </div>
                  </div>
                ) : null;
              })()}
            </div>

            
            <div>
              <label className="block text-slate-700 dark:text-slate-300 text-sm font-medium mb-2">
                {t('studentsPage.formJoinDate')}
              </label>
              <input 
                type="date"
                name="joinDate" 
                value={formData.joinDate}
                onChange={handleChange}
                className="w-full bg-slate-100/80 dark:bg-slate-800/40 border border-slate-200 dark:border-slate-600 rounded-lg px-4 py-2.5 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-forma-500"
              />
            </div>
          </div>
        </div>
        
        <div className="flex justify-end space-x-4">
          <Link 
            to="/students"
            className="px-4 py-2 bg-slate-100/80 dark:bg-slate-800/40 hover:bg-slate-100 dark:hover:bg-slate-800/80 transition-colors rounded-lg text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:text-slate-50"
          >
            {t('trainerForm.cancel')}
          </Link>
          <button
            type="submit"
            className={clsx(FORMA_LINK_BTN, 'justify-center')}
          >
            {isEditing ? t('trainerForm.submitUpdate') : t('trainerForm.submitAdd')}
          </button>
        </div>
      </form>
    </div>
  );
};

const StudentsPage = () => {
  const location = useLocation();
  const { t } = useLanguage();
  const queryClient = useQueryClient();
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [studentsPerPage] = useState(10);
  const [activeTab, setActiveTab] = useState('all');

  const [selectedStudents, setSelectedStudents] = useState([]);
  const [isSelectAll, setIsSelectAll] = useState(false);
  const [showBulkDeleteModal, setShowBulkDeleteModal] = useState(false);

  const { data: studentsFromApi = [], isLoading: studentsLoading } = useQuery(
    FORMATION_STUDENTS_CATALOG_KEY,
    async () => {
      const res = await formationAPI.getStudents({ page: 1, limit: 500 });
      return res.items || [];
    },
    { staleTime: 15000 }
  );

  const { data: enrollmentBundle } = useQuery(FORMATION_ENROLLMENT_BUNDLE_KEY, fetchFormationEnrollmentBundle, {
    staleTime: 5000,
  });

  const { data: coursesListRaw } = useQuery(
    ['courses', 'lite-list'],
    () => spaEntityAPI.list('courses'),
    { staleTime: 60_000, retry: 1 }
  );

  const coursesCatalog = useMemo(() => coursesListRaw || [], [coursesListRaw]);

  useEffect(() => {
    void invalidateFormationGraph(queryClient);
  }, [location.pathname, queryClient]);

  const enrollmentByStudent = useMemo(() => {
    const m = {};
    for (const e of enrollmentBundle?.enrollments || []) {
      if (e.status !== 'active' || !e.studentId) continue;
      if (!m[e.studentId]) m[e.studentId] = [];
      m[e.studentId].push(e);
    }
    return m;
  }, [enrollmentBundle?.enrollments]);

  const batchById = useMemo(() => {
    const m = {};
    for (const b of enrollmentBundle?.batches || []) {
      if (b?.id) m[b.id] = b;
    }
    return m;
  }, [enrollmentBundle?.batches]);

  const studentsWithCourses = useMemo(() => {
    const list = Array.isArray(studentsFromApi) ? studentsFromApi : [];
    return list.map((student) => {
      const courseRef = student.currentCourseId ?? student.currentCourse;
      if (courseRef && typeof courseRef === 'string') {
        const courseData = coursesCatalog.find((c) => String(c.id) === String(courseRef));
        if (courseData) {
          return {
            ...student,
            currentCourse: {
              ...courseData,
              type: courseData.title ?? courseData.name,
            },
          };
        }
        return { ...student, currentCourse: null };
      }
      if (courseRef && typeof courseRef === 'object') {
        const co = student.currentCourse;
        return typeof co?.type !== 'undefined'
          ? student
          : {
              ...student,
              currentCourse: co ? { ...co, type: co.title ?? co.name } : student.currentCourse,
            };
      }
      return { ...student, currentCourse: null };
    });
  }, [studentsFromApi, coursesCatalog]);

  const stats = useMemo(
    () => [
      {
        title: t('studentsPage.statRegisteredTitle'),
        value: studentsWithCourses.length.toString(),
        hint: t('studentsPage.statRegisteredHint'),
        icon: UserGroupIcon,
        iconWrap:
          'bg-forma-100 text-forma-700 shadow-inner dark:bg-forma-950/50 dark:text-forma-400',
      },
      {
        title: t('studentsPage.statActiveTitle'),
        value: studentsWithCourses.filter((s) => s.status === 'active').length.toString(),
        hint: t('studentsPage.statActiveHint'),
        icon: CheckCircleIcon,
        iconWrap:
          'bg-emerald-100 text-emerald-800 shadow-inner dark:bg-emerald-950/45 dark:text-emerald-400',
      },
      {
        title: t('studentsPage.statNoCourseTitle'),
        value: studentsWithCourses.filter((s) => !s.currentCourse).length.toString(),
        hint: t('studentsPage.statNoCourseHint'),
        icon: XCircleIcon,
        iconWrap: 'bg-rose-100 text-rose-800 shadow-inner dark:bg-rose-950/40 dark:text-rose-400',
      },
    ],
    [studentsWithCourses, t]
  );

  const tabs = useMemo(
    () => [
      { id: 'all', name: t('studentsPage.tabAll') },
      { id: 'active', name: t('studentsPage.tabActive') },
      { id: 'suspended', name: t('studentsPage.tabSuspended') },
      { id: 'inactive', name: t('studentsPage.tabInactive') },
      { id: 'no_course', name: t('studentsPage.tabNoCourse') },
    ],
    [t]
  );

  const filteredStudents = studentsWithCourses.filter((student) => {
    const q = (searchTerm || '').trim().toLowerCase();
    const matchesSearch =
      (student.name && student.name.toLowerCase().includes(q)) ||
      (student.studentCode && String(student.studentCode).toLowerCase().includes(q)) ||
      (student.email && String(student.email).toLowerCase().includes(q));

    if (!matchesSearch) return false;

    if (activeTab === 'all') return true;
    if (activeTab === 'active') return student.status === 'active';
    if (activeTab === 'suspended') return student.status === 'suspended';
    if (activeTab === 'inactive') return student.status === 'inactive';
    if (activeTab === 'no_course') return !student.currentCourse;
    return true;
  });

  const tabCounts = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();
    const base = studentsWithCourses.filter((student) => {
      if (!q) return true;
      return (
        (student.name && student.name.toLowerCase().includes(q)) ||
        (student.studentCode && String(student.studentCode).toLowerCase().includes(q)) ||
        (student.email && String(student.email).toLowerCase().includes(q))
      );
    });
    return {
      all: base.length,
      active: base.filter((s) => s.status === 'active').length,
      suspended: base.filter((s) => s.status === 'suspended').length,
      inactive: base.filter((s) => s.status === 'inactive').length,
      no_course: base.filter((s) => !s.currentCourse).length,
    };
  }, [studentsWithCourses, searchTerm]);

  const paginatedStudents = filteredStudents.slice((currentPage - 1) * studentsPerPage, currentPage * studentsPerPage);
  const totalStudents = filteredStudents.length;
  const totalPages = Math.ceil(totalStudents / studentsPerPage);
  const pageSliceLabel =
    totalStudents === 0
      ? '—'
      : `${Math.min((currentPage - 1) * studentsPerPage + 1, totalStudents)}–${Math.min(currentPage * studentsPerPage, totalStudents)}`;

  const pageNav = useMemo(
    () => paginationItems(Math.min(Math.max(currentPage, 1), Math.max(totalPages, 1)), Math.max(totalPages, 1)),
    [currentPage, totalPages]
  );

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1);
  };

  const handleDelete = async (studentId) => {
    if (!(await confirmDialog(t('studentsPage.confirmDeleteStudent')))) return;
    try {
      await formationAPI.deleteStudent(studentId);
      await invalidateFormationGraph(queryClient);
    } catch (error) {
      console.error('Erreur lors de la suppression:', error);
      notify.show(t('studentsPage.deleteErrorGeneric'));
    }
  };

  // Multi-select functions
  const handleSelectStudent = (studentId) => {
    setSelectedStudents(prev => {
      if (prev.includes(studentId)) {
        // Remove from selection
        return prev.filter(id => id !== studentId);
      } else {
        // Add to selection
        return [...prev, studentId];
      }
    });
  };

  const handleSelectAll = () => {
    if (isSelectAll) {
      // Deselect all
      setSelectedStudents([]);
      setIsSelectAll(false);
    } else {
      // Select all visible students
      const visibleStudentIds = paginatedStudents.map(s => s.id);
      setSelectedStudents(visibleStudentIds);
      setIsSelectAll(true);
    }
  };

  const handleBulkDelete = async () => {
    try {
      const ids = [...selectedStudents];
      for (const studentId of ids) {
        await formationAPI.deleteStudent(studentId);
      }

      await invalidateFormationGraph(queryClient);

      setSelectedStudents([]);
      setIsSelectAll(false);
      setShowBulkDeleteModal(false);
    } catch (error) {
      console.error('Error deleting students:', error);
      notify.show(t('studentsPage.bulkDeleteError'));
    }
  };

  // Auto-update select all state based on selected students
  useEffect(() => {
    if (selectedStudents.length === 0) {
      setIsSelectAll(false);
    } else {
      const visibleStudentIds = paginatedStudents.map(s => s.id);
      const allVisibleSelected = visibleStudentIds.every(id => selectedStudents.includes(id));
      setIsSelectAll(allVisibleSelected && visibleStudentIds.length > 0);
    }
  }, [selectedStudents, paginatedStudents]);

  const formaLinkBtn = FORMA_LINK_BTN;

  return (
    <>
      <Routes>
        <Route index element={
          <div className="space-y-6">
            <PageHeader
              title={t('studentsPageTitle')}
              description={t('studentsPage.description')}
              actions={
                <Link to="/students/add" className={formaLinkBtn}>
                  <PlusIcon className="h-5 w-5" />
                  <span>{t('studentsPage.newStudent')}</span>
                </Link>
              }
            />

            <UiCard padding={false} className="overflow-hidden">
              <div className="border-b border-slate-200/90 bg-gradient-to-br from-slate-50 via-white to-slate-50/80 dark:border-slate-700 dark:from-slate-950 dark:via-slate-900 dark:to-slate-950">
                <div className="grid gap-6 p-6 sm:grid-cols-3 sm:gap-5">
                  {stats.map((stat, index) => (
                    <motion.div
                      key={stat.title}
                      layout
                      initial={{ opacity: 0, y: 12 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ type: 'spring', stiffness: 380, damping: 28, delay: index * 0.06 }}
                      whileHover={{ y: -3 }}
                      className="group relative rounded-2xl border border-slate-200/80 bg-white/80 p-[1px] shadow-sm backdrop-blur-sm dark:border-slate-700/80 dark:bg-slate-950/55"
                    >
                      <div className="relative overflow-hidden rounded-[0.9375rem] bg-gradient-to-b from-white/90 to-white/35 p-4 dark:from-slate-950/95 dark:to-slate-950/65">
                        <div
                          aria-hidden
                          className="pointer-events-none absolute -right-6 -top-6 h-24 w-24 rounded-full bg-gradient-to-br from-forma-200/35 to-transparent opacity-70 blur-2xl transition group-hover:opacity-100 dark:from-forma-600/25"
                        />
                        <div className="relative flex gap-4">
                          <div
                            className={clsx(
                              'relative flex h-12 w-12 shrink-0 items-center justify-center rounded-xl ring-2 ring-white/70 dark:ring-slate-800/80',
                              stat.iconWrap
                            )}
                          >
                            <stat.icon className="h-6 w-6" />
                          </div>
                          <div className="min-w-0 flex-1">
                            <p className="text-[11px] font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400">
                              {stat.title}
                            </p>
                            <p className="mt-0.5 text-3xl font-bold tabular-nums tracking-tight text-slate-900 dark:text-white">
                              {stat.value}
                            </p>
                            <p className="mt-1 border-t border-slate-100 pt-2 text-xs text-slate-500 dark:border-slate-700/70 dark:text-slate-400">
                              {stat.hint}
                            </p>
                          </div>
                        </div>
                      </div>
                    </motion.div>
                  ))}
                </div>
              </div>

              <div className="border-b border-slate-200 bg-white/40 px-5 py-4 dark:border-slate-700 dark:bg-slate-900/35">
                <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-end">
                  <FilterSelect
                    id="student-status-filter"
                    label={t('studentsPage.statusLabel')}
                    value={activeTab}
                    onChange={(v) => {
                      setActiveTab(v);
                      setCurrentPage(1);
                    }}
                    options={tabs.map((tab) => ({
                      id: tab.id,
                      name: tab.name,
                      count: tabCounts[tab.id],
                    }))}
                    className="sm:min-w-[11rem]"
                  />
                  <div className="relative min-w-0 flex-1 sm:min-w-[14rem]">
                    <label htmlFor="student-search" className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                      {t('studentsPage.searchSrOnly')}
                    </label>
                    <MagnifyingGlassIcon className="pointer-events-none absolute bottom-2.5 left-3 h-5 w-5 text-slate-400" />
                    <input
                      id="student-search"
                      type="search"
                      autoComplete="off"
                      placeholder={t('studentsPage.searchPlaceholder')}
                      value={searchTerm}
                      onChange={handleSearch}
                      className={clsx(
                        'w-full rounded-xl border border-slate-200 bg-white py-2.5 pl-10 text-sm text-slate-900 shadow-sm placeholder:text-slate-400 focus:border-forma-500 focus:outline-none focus:ring-2 focus:ring-forma-500/25 dark:border-slate-600 dark:bg-slate-950 dark:text-slate-100 dark:placeholder:text-slate-500',
                        searchTerm.trim() ? 'pr-10' : 'pr-4'
                      )}
                    />
                    {searchTerm.trim() !== '' ? (
                      <button
                        type="button"
                        aria-label={t('studentsPage.clearSearchAria')}
                        onClick={() => {
                          setSearchTerm('');
                          setCurrentPage(1);
                        }}
                        className="absolute bottom-1.5 right-2 rounded-lg p-1 text-slate-400 transition hover:bg-slate-100 hover:text-slate-700 dark:hover:bg-slate-800 dark:hover:text-slate-200"
                      >
                        <XMarkIcon className="h-5 w-5" />
                      </button>
                    ) : null}
                  </div>
                </div>
              </div>

              {(searchTerm.trim() !== '' || activeTab !== 'all') && (
                <div className="flex flex-wrap items-center gap-2 border-b border-forma-200/60 bg-forma-50/50 px-5 py-2.5 dark:border-forma-900/35 dark:bg-forma-950/25">
                  <span className="text-[11px] font-bold uppercase tracking-wider text-forma-800 dark:text-forma-400">
                    {t('studentsPage.filtersActive')}
                  </span>
                  {searchTerm.trim() !== '' ? (
                    <span className="inline-flex items-center gap-1 rounded-full border border-forma-200/70 bg-white px-2.5 py-1 text-xs font-medium text-slate-700 shadow-sm dark:border-forma-900/60 dark:bg-slate-950 dark:text-slate-200">
                      « {searchTerm.trim()} »
                    </span>
                  ) : null}
                  {activeTab !== 'all' ? (
                    <span className="inline-flex items-center gap-1 rounded-full border border-forma-200/70 bg-white px-2.5 py-1 text-xs font-medium text-slate-700 shadow-sm dark:border-forma-900/60 dark:bg-slate-950 dark:text-slate-200">
                      {tabs.find((tab) => tab.id === activeTab)?.name}
                    </span>
                  ) : null}
                  <button
                    type="button"
                    onClick={() => {
                      setSearchTerm('');
                      setActiveTab('all');
                      setCurrentPage(1);
                    }}
                    className="ml-auto rounded-lg border border-slate-200 bg-white px-2.5 py-1 text-xs font-semibold text-slate-600 transition hover:bg-slate-50 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-300 dark:hover:bg-slate-800"
                  >
                    {t('studentsPage.resetFilters')}
                  </button>
                </div>
              )}
              {selectedStudents.length > 0 && (
                <div className="flex flex-wrap items-center justify-between gap-3 border-b border-forma-200/80 bg-forma-50/90 px-5 py-3 dark:border-forma-900/50 dark:bg-forma-950/25">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-sm font-medium text-slate-800 dark:text-slate-100">
                      {selectedStudents.length === 1
                        ? t('studentsPage.selectedSingle')
                        : t('studentsPage.selectedPlural', { n: selectedStudents.length })}
                    </span>
                    <button
                      type="button"
                      onClick={() => {
                        setSelectedStudents([]);
                        setIsSelectAll(false);
                      }}
                      className="rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs font-medium text-slate-700 transition hover:bg-slate-50 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-300 dark:hover:bg-slate-800"
                    >
                      {t('studentsPage.deselectAll')}
                    </button>
                  </div>
                  <button
                    type="button"
                    onClick={() => setShowBulkDeleteModal(true)}
                    className="inline-flex items-center gap-2 rounded-lg bg-red-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-red-700"
                  >
                    <TrashIcon className="h-4 w-4" />
                    {t('studentsPage.deleteSelected', { n: selectedStudents.length })}
                  </button>
                </div>
              )}

              {studentsLoading && studentsFromApi.length === 0 ? (
                <div className="flex items-center justify-center px-6 py-20">
                  <div className="h-12 w-12 animate-spin rounded-full border-b-2 border-forma-500" />
                </div>
              ) : paginatedStudents.length === 0 ? (
                <div className="flex flex-col items-center justify-center gap-5 px-6 py-16 text-center">
                  <div className="flex h-20 w-20 items-center justify-center rounded-2xl border border-dashed border-slate-200 bg-slate-50 dark:border-slate-700 dark:bg-slate-800/60">
                    <UserGroupIcon className="h-10 w-10 text-forma-500/70 dark:text-forma-400/80" />
                  </div>
                  <div className="max-w-md space-y-2">
                    <h3 className="text-lg font-semibold text-slate-900 dark:text-white">{t('studentsPage.emptyTitle')}</h3>
                    <p className="text-sm leading-relaxed text-slate-600 dark:text-slate-400">
                      {t('studentsPage.emptyBody')}
                    </p>
                    <button
                      type="button"
                      onClick={() => {
                        setSearchTerm('');
                        setActiveTab('all');
                        setCurrentPage(1);
                      }}
                      className="mx-auto rounded-xl border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700 shadow-sm transition hover:bg-slate-50 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800"
                    >
                      {t('studentsPage.clearFiltersBtn')}
                    </button>
                  </div>
                  <Link to="/students/add" className={formaLinkBtn}>
                    <PlusIcon className="h-5 w-5" />
                    <span>{t('studentsPage.addStudent')}</span>
                  </Link>
                </div>
              ) : (
                <>
                  <div className="border-b border-slate-200 bg-gradient-to-r from-white to-slate-50/95 px-6 py-4 dark:border-slate-700 dark:from-slate-900 dark:to-slate-950/90">
                    <h2 className="text-lg font-semibold tracking-tight text-slate-900 dark:text-white">
                      {t('studentsPage.listHeading')}
                    </h2>
                    <p className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-sm text-slate-600 dark:text-slate-400">
                      <span>
                        <span className="font-semibold tabular-nums text-slate-900 dark:text-slate-100">
                          {totalStudents}
                        </span>{' '}
                        {t(
                          totalStudents === 1
                            ? 'studentsPage.resultsSingular'
                            : 'studentsPage.resultsPlural'
                        )}
                      </span>
                      <span className="text-slate-300 dark:text-slate-600" aria-hidden>
                        ·
                      </span>
                      <span>
                        {t('studentsPage.rangeOn')}{' '}
                        <span className="font-medium tabular-nums text-slate-800 dark:text-slate-200">
                          {pageSliceLabel}
                        </span>{' '}
                        {t('studentsPage.onTotal')}{' '}
                        <span className="font-medium tabular-nums text-slate-800 dark:text-slate-200">
                          {totalStudents}
                        </span>
                      </span>
                      <span className="text-slate-300 dark:text-slate-600" aria-hidden>
                        ·
                      </span>
                      <span className="text-slate-500 dark:text-slate-500">
                        {studentsPerPage} {t('studentsPage.perPage')}
                      </span>
                    </p>
                  </div>

                  <div className="overflow-x-auto">
                    <table className="w-full min-w-[720px] border-collapse text-left">
                      <thead className="sticky top-0 z-10 border-b border-slate-200 bg-white/95 text-[11px] font-bold uppercase tracking-wider text-slate-500 shadow-[0_1px_0_rgba(15,23,42,0.06)] backdrop-blur-md dark:border-slate-700 dark:bg-slate-900/95 dark:text-slate-400">
                        <tr>
                          <th scope="col" className="w-12 px-4 py-3.5">
                            <span className="sr-only">{t('studentsPage.srSelection')}</span>
                            <input
                              type="checkbox"
                              checked={isSelectAll}
                              onChange={handleSelectAll}
                              aria-label={t('studentsPage.selectAllPageAria')}
                              className="h-4 w-4 rounded border-slate-300 text-forma-600 focus:ring-forma-500 dark:border-slate-600 dark:bg-slate-900"
                            />
                          </th>
                          <th scope="col" className="px-4 py-3.5 font-bold">
                            {t('studentsPage.colStudent')}
                          </th>
                          <th scope="col" className="px-4 py-3.5 font-bold">
                            {t('studentsPage.colContact')}
                          </th>
                          <th scope="col" className="px-4 py-3.5 font-bold">
                            {t('studentsPage.colFormation')}
                          </th>
                          <th scope="col" className="px-4 py-3.5 font-bold">
                            {t('studentsPage.colStatus')}
                          </th>
                          <th scope="col" className="px-4 py-3.5 text-right font-bold">
                            {t('studentsPage.colActions')}
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {paginatedStudents.map((student, rowIdx) => (
                          <motion.tr
                            key={student.id}
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            transition={{ delay: Math.min(rowIdx * 0.03, 0.35) }}
                            className={clsx(
                              'border-b border-slate-100 transition-colors dark:border-slate-800/80',
                              rowIdx % 2 === 1 && 'bg-slate-50/45 dark:bg-slate-900/40',
                              'hover:bg-forma-50/50 dark:hover:bg-forma-950/20',
                              selectedStudents.includes(student.id) &&
                                'bg-forma-100/55 ring-1 ring-inset ring-forma-200/80 dark:bg-forma-950/40 dark:ring-forma-800/60'
                            )}
                          >
                            <td className="whitespace-nowrap px-4 py-3.5">
                              <input
                                type="checkbox"
                                checked={selectedStudents.includes(student.id)}
                                onChange={() => handleSelectStudent(student.id)}
                                aria-label={t('studentsPage.selectStudentSr', { name: student.name })}
                                className="h-4 w-4 rounded border-slate-300 text-forma-600 focus:ring-forma-500 dark:border-slate-600 dark:bg-slate-900"
                              />
                            </td>
                            <td className="whitespace-nowrap px-4 py-3.5">
                              <div className="flex items-center">
                                <div className="mr-3 flex h-10 w-10 shrink-0 items-center justify-center overflow-hidden rounded-xl bg-forma-100 dark:bg-forma-950/50">
                                  {student.photo ? (
                                    <img
                                      src={student.photo}
                                      alt={student.name}
                                      className="h-10 w-10 object-cover"
                                    />
                                  ) : (
                                    <AcademicCapIcon className="h-5 w-5 text-forma-600 dark:text-forma-400" />
                                  )}
                                </div>
                                <div>
                                  <div className="text-sm font-medium text-slate-900 dark:text-slate-50">
                                    {student.name}
                                  </div>
                                  <div className="text-xs text-slate-600 dark:text-slate-400">{student.studentCode}</div>
                                </div>
                              </div>
                            </td>
                            <td className="whitespace-nowrap px-4 py-3.5">
                              <div className="text-sm text-slate-700 dark:text-slate-300">{student.phone}</div>
                              <div className="text-xs text-slate-600 dark:text-slate-400">{student.email}</div>
                            </td>
                            <td className="whitespace-nowrap px-4 py-3.5">
                              {(() => {
                                const activeEnrollments = enrollmentByStudent[student.id] || [];

                                if (activeEnrollments.length === 0) {
                                  return (
                                    <span className="rounded-full bg-rose-100 px-2.5 py-1 text-xs font-medium text-rose-800 dark:bg-rose-500/15 dark:text-rose-400">
                                      {t('studentsPage.noEnrollment')}
                                    </span>
                                  );
                                }

                                if (activeEnrollments.length === 1) {
                                  const batch = batchById[activeEnrollments[0].batchId];
                                  if (!batch) {
                                    return (
                                      <span className="rounded-full bg-rose-100 px-2.5 py-1 text-xs font-medium text-rose-800 dark:bg-rose-500/15 dark:text-rose-400">
                                        {t('studentsPage.groupNotFound')}
                                      </span>
                                    );
                                  }

                                  return (
                                    <div>
                                      <div className="text-sm text-slate-700 dark:text-slate-300">{batch.courseName}</div>
                                      <div className="text-xs text-slate-600 dark:text-slate-400">
                                        {(batch.days || []).join(', ')} • {batch.startTime}-{batch.endTime}
                                      </div>
                                    </div>
                                  );
                                }

                                return (
                                  <div>
                                    <div className="text-sm text-slate-700 dark:text-slate-300">
                                      {t('studentsPage.enrollmentsCount', { n: activeEnrollments.length })}
                                    </div>
                                    <div className="text-xs text-slate-600 dark:text-slate-400">
                                      {t('studentsPage.multipleEnrollments')}
                                    </div>
                                  </div>
                                );
                              })()}
                            </td>
                            <td className="whitespace-nowrap px-4 py-3.5">
                              <span
                                className={clsx(
                                  'rounded-full px-2.5 py-1 text-xs font-medium',
                                  student.status === 'active' &&
                                    'bg-emerald-100 text-emerald-800 dark:bg-emerald-500/15 dark:text-emerald-400',
                                  student.status === 'suspended' &&
                                    'bg-amber-100 text-amber-800 dark:bg-amber-500/15 dark:text-amber-400',
                                  student.status !== 'active' &&
                                    student.status !== 'suspended' &&
                                    'bg-slate-100 text-slate-600 dark:bg-slate-600/25 dark:text-slate-400'
                                )}
                              >
                                {student.status === 'active'
                                  ? t('studentsPage.statusActiveLbl')
                                  : student.status === 'suspended'
                                    ? t('studentsPage.statusSuspendedLbl')
                                    : t('studentsPage.statusInactiveLbl')}
                              </span>
                            </td>
                            <td className="whitespace-nowrap px-4 py-3.5 text-right text-sm">
                              <div className="flex justify-end gap-1.5">
                                <Link
                                  to={`/students/${student.id}`}
                                  className="rounded-lg border border-slate-200 bg-white p-2 text-slate-600 shadow-sm transition hover:bg-slate-50 hover:text-forma-600 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-400 dark:hover:bg-slate-800 dark:hover:text-forma-400"
                                  title={t('commonActions.viewDetails')}
                                >
                                  <EyeIcon className="h-4 w-4" />
                                </Link>
                                <Link
                                  to={`/students/edit/${student.id}`}
                                  className="rounded-lg border border-slate-200 bg-white p-2 text-slate-600 shadow-sm transition hover:bg-slate-50 hover:text-amber-600 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-400 dark:hover:bg-slate-800"
                                  title={t('commonActions.edit')}
                                >
                                  <PencilIcon className="h-4 w-4" />
                                </Link>
                                <button
                                  type="button"
                                  onClick={() => handleDelete(student.id)}
                                  className="rounded-lg border border-slate-200 bg-white p-2 text-red-600 shadow-sm transition hover:bg-red-50 dark:border-slate-600 dark:bg-slate-900 dark:text-red-400 dark:hover:bg-red-950/30"
                                  title={t('commonActions.delete')}
                                >
                                  <TrashIcon className="h-4 w-4" />
                                </button>
                              </div>
                            </td>
                          </motion.tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {totalStudents > studentsPerPage && (
                    <div className="flex flex-col gap-4 border-t border-slate-200 px-6 py-4 dark:border-slate-600 sm:flex-row sm:items-center sm:justify-between">
                      <p className="text-center text-sm text-slate-600 dark:text-slate-400 sm:text-left">
                        {t('studentsPage.paginationSummary', {
                          range: pageSliceLabel,
                          total: totalStudents,
                          page: currentPage,
                          pages: totalPages,
                        })}
                      </p>
                      <nav
                        className="flex flex-wrap items-center justify-center gap-1.5 sm:justify-end"
                        aria-label={t('studentsPage.paginationLabel')}
                      >
                        <button
                          type="button"
                          onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                          disabled={currentPage === 1}
                          className="inline-flex items-center gap-1 rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-sm font-medium text-slate-700 shadow-sm transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-40 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800"
                        >
                          <ChevronLeftIcon className="h-4 w-4" aria-hidden />
                          {t('studentsPage.prev')}
                        </button>
                        <div className="mx-1 flex flex-wrap items-center justify-center gap-1">
                          {pageNav.map((item, idx) =>
                            item.type === 'ellipsis' ? (
                              <span
                                key={`ellipsis-${idx}`}
                                className="px-2 font-medium text-slate-400 dark:text-slate-500"
                                aria-hidden
                              >
                                …
                              </span>
                            ) : (
                              <button
                                key={item.page}
                                type="button"
                                onClick={() => setCurrentPage(item.page)}
                                className={clsx(
                                  'min-w-[2.5rem] rounded-lg px-2 py-1.5 text-sm font-semibold tabular-nums transition',
                                  item.page === currentPage
                                    ? 'bg-forma-600 text-white shadow-sm'
                                    : 'border border-transparent text-slate-700 hover:bg-slate-100 dark:text-slate-200 dark:hover:bg-slate-800'
                                )}
                              >
                                {item.page}
                              </button>
                            )
                          )}
                        </div>
                        <button
                          type="button"
                          onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                          disabled={currentPage === totalPages}
                          className="inline-flex items-center gap-1 rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-sm font-medium text-slate-700 shadow-sm transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-40 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800"
                        >
                          {t('studentsPage.next')}
                          <ChevronRightIcon className="h-4 w-4" aria-hidden />
                        </button>
                      </nav>
                    </div>
                  )}
                </>
              )}
            </UiCard>
          </div>
        }
      />
      <Route
        path="add"
        element={
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 20 }}
            transition={{ duration: 0.3 }}
          >
            <div className="mb-6">
              <Link to="/students" className="text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:text-slate-50 flex items-center">
                <ArrowLeftIcon className="h-5 w-5 mr-2" />
                {t('studentsPage.backToList')}
              </Link>
            </div>
            <StudentForm />
          </motion.div>
        }
      />
      <Route
        path="edit/:id"
        element={
          <EditStudentRoute />
        }
      />
      <Route
        path=":id"
        element={
          <ViewStudentRoute />
        }
      />
      </Routes>

      {/* Bulk Delete Confirmation Modal */}
      {showBulkDeleteModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="rounded-2xl border border-slate-200 bg-white p-6 shadow-xl dark:border-slate-600 dark:bg-slate-900 w-full max-w-md"
          >
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 bg-red-500/20 rounded-lg">
                <TrashIcon className="h-6 w-6 text-red-400" />
              </div>
              <h3 className="text-xl font-semibold text-slate-900 dark:text-slate-50">
                {t('studentsPage.bulkDeleteTitle')}
              </h3>
            </div>
            <p className="text-slate-600 dark:text-slate-400 mb-6">
              {selectedStudents.length === 1
                ? t('studentsPage.bulkDeleteAskOne')
                : t('studentsPage.bulkDeleteAskMany', { n: selectedStudents.length })}
            </p>
            <div className="flex gap-3 w-full">
              <button
                onClick={() => setShowBulkDeleteModal(false)}
                className="flex-1 px-4 py-2 bg-slate-50 dark:bg-slate-800/80 text-slate-900 dark:text-slate-50 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
              >
                {t('trainerForm.cancel')}
              </button>
              <button
                onClick={handleBulkDelete}
                className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
              >
                {t('commonActions.delete')}
              </button>
            </div>
          </motion.div>
        </div>
      )}
    </>
  );
};

// Route components for edit and view student
const EditStudentRoute = () => {
  const { t } = useLanguage();
  const { id } = useParams();
  const { data: student, isLoading, isError } = useQuery(
    ['formation', 'student', id],
    () => formationAPI.getStudentById(id),
    { enabled: Boolean(id), retry: false }
  );

  if (isLoading) {
    return <div className="text-slate-900 dark:text-slate-50">{t('studentsPage.loading')}</div>;
  }

  if (isError || !student?.id) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-slate-700 dark:text-slate-300">
          <p>{t('studentsPage.notFoundMsg', { id })}</p>
          <Link to="/students" className="text-blue-400 hover:text-blue-300 mt-2 inline-block">
            {t('studentsPage.backToStudentsList')}
          </Link>
        </div>
      </div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: 20 }}
      transition={{ duration: 0.3 }}
    >
      <div className="mb-6">
        <Link to="/students" className="text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:text-slate-50 flex items-center">
          <ArrowLeftIcon className="h-5 w-5 mr-2" />
          {t('studentsPage.backToList')}
        </Link>
      </div>
      <StudentForm isEditing={true} studentData={student} />
    </motion.div>
  );
};

const ViewStudentRoute = () => {
  const { t } = useLanguage();
  const { id } = useParams();

  const { data: studentRow, isLoading, isError } = useQuery(
    ['formation', 'student', id],
    () => formationAPI.getStudentById(id),
    { enabled: Boolean(id), retry: false }
  );

  const { data: coursesListRaw } = useQuery(
    ['courses', 'lite-list'],
    () => spaEntityAPI.list('courses'),
    { staleTime: 60_000 }
  );

  const coursesCatalog = useMemo(() => coursesListRaw || [], [coursesListRaw]);

  const student = useMemo(() => {
    if (!studentRow) return null;
    const courseRef = studentRow.currentCourseId ?? studentRow.currentCourse;
    if (courseRef && typeof courseRef === 'string') {
      const courseData = coursesCatalog.find((c) => String(c.id) === String(courseRef));
      if (courseData) {
        return {
          ...studentRow,
          currentCourse: {
            ...courseData,
            type: courseData.title ?? courseData.name,
          },
        };
      }
      return { ...studentRow, currentCourse: null };
    }
    if (courseRef && typeof courseRef === 'object') {
      const co = studentRow.currentCourse;
      return typeof co?.type !== 'undefined'
        ? studentRow
        : {
            ...studentRow,
            currentCourse: co ? { ...co, type: co.title ?? co.name } : co,
          };
    }
    return { ...studentRow, currentCourse: null };
  }, [studentRow, coursesCatalog]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-slate-900 dark:text-slate-50">{t('studentsPage.loadingDetail')}</div>
      </div>
    );
  }

  if (isError || !student?.id) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-slate-700 dark:text-slate-300">
          <p>{t('studentsPage.notFoundMsg', { id })}</p>
          <Link to="/students" className="text-blue-400 hover:text-blue-300 mt-2 inline-block">
            {t('studentsPage.backToStudentsList')}
          </Link>
        </div>
      </div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: 20 }}
      transition={{ duration: 0.3 }}
    >
      <div className="mb-6">
        <Link to="/students" className="text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:text-slate-50 flex items-center">
          <ArrowLeftIcon className="h-5 w-5 mr-2" />
          {t('studentsPage.backToList')}
        </Link>
      </div>
      <StudentDetail student={student} />
    </motion.div>
  );
};

export default StudentsPage; 